import type { AudioAsset, PhotoAsset, Species } from '../../types';
import { el, ensureStyle } from '../dom';
import { getSpecies, speciesList } from '../../core/region';
import { stopAll } from '../../core/media/audio';
import {
  compareFacets,
  defaultFacet,
  facetCount,
  FACET_LABEL,
  type Facet,
} from '../../core/compare';
import { getCompareLayout, setCompareLayout, type CompareLayout } from '../prefs';
import { icon } from './icons';
import { photoCredit, speciesPhoto } from './species-photo';
import { waveformPlayer, type WaveformHandle } from './waveform-player';
import { openSpeciesPicker } from './species-picker';
import { sourceLabel } from '../../core/attributions';
import { t } from '../../strings';

// The side-by-side view: two birds, everything they have, on one parallel
// scroll.
//
// Everything else in Syrinx shows one bird at a time, which means every
// comparison a learner wants to make has to be made across a navigation and
// out of memory — and the whole difficulty of bird identification is that the
// memory is exactly what isn't there yet. Willow against Alder Flycatcher,
// Song against Lincoln's Sparrow: the pair IS the lesson, and a field guide
// has always known it.
//
// ---- One scroll, not four screens ------------------------------------------
//
// This used to be one facet at a time behind a row of tabs — photo, song, call,
// alarm — which made the screen a set of four comparisons you flicked between
// rather than one comparison you read down. Two birds are not four questions.
// So every facet is on screen at once, in a single column of sections:
//
//     PHOTO   [ bird A ] [ bird B ]
//     SONG    [ bird A ] [ bird B ]
//     CALL    [ bird A ] [ bird B ]
//     ALARM   [ bird A ] [ bird B ]
//
// The parallelism is STRUCTURAL rather than synchronised. One scroll container
// holds both birds, and each section is its own small grid holding that
// section's two cells, so "the same kind of thing, for both birds, on screen
// together" is true by construction — there is no second scroller to keep in
// step, and so nothing that can drift out of step.
//
// ---- Rotating the split ----------------------------------------------------
//
// A photograph and a recording want opposite arrangements. Two pictures at
// half width are still two pictures; a sound strip at half width is a smear,
// because it is drawn against time and the time axis is the thing being
// compared. With both on screen at once there is no arrangement that is right
// for everything, so the split turns: COLUMNS puts the pair left and right,
// ROWS puts it one above the other with the full width each. It is a
// per-section grid template and nothing else — the sections, the scroll and
// the pairing are identical either way, so rotating cannot break the
// parallelism, and it keeps every strip, every stepped-to photo and the scroll
// position exactly where they were. The choice is remembered per device
// (src/app/prefs.ts).
//
// ---- Mounted two ways ------------------------------------------------------
//
// As a route (#/compare/:left/:right, src/app/views/compare.ts), which is what
// the Library and the species page use, and what makes a comparison a URL. And
// as an OVERLAY over whatever is already on screen, which is what a quiz uses:
// a round lives entirely in its view's closure, so routing away from a
// half-finished round to look at two birds would throw the round away. Same
// surface, two mounts — see `openCompare`.

ensureStyle(
  'compare-css',
  `
  /* flex:1 against the router outlet, which is a column flex container for
     exactly this — see base.css. The FRAME does not scroll; the section list
     inside it does, which is what keeps the bar and the two names in place
     while you read down the pair. */
  .compare { flex:1; display:flex; flex-direction:column; min-height:0;
    padding:6px var(--pad) 0; gap:6px; }
  /* Routed, the frame needs its ceiling stated outright. The outlet cannot
     give it one: #app carries min-height:100dvh rather than a height, so a
     page taller than the screen grows #app, the outlet and this — and an
     inner scroller whose parent grows to fit it never scrolls at all.
     
     flex:none along with it, and that half is the load-bearing half: a flex
     item sized flex:1 takes its main size from the flex algorithm and
     IGNORES height outright, so stating the ceiling without also stepping out
     of the flex sizing does nothing at all. (The overlay mount needs none of
     this: position:fixed inset:0 is already a definite box, which is why the
     rule is scoped to the outlet.) */
  .outlet > .compare { flex:none;
    height:calc(100vh - var(--navbar-h));
    height:calc(100dvh - var(--navbar-h)); }

  /* ---- The bar ----------------------------------------------------------
     Back on the left, rotate on the right, and nothing in between: the two
     names below say what this screen is, and there is no longer a row of tabs
     to make room for. */
  .compare .cbar { flex:none; display:flex; align-items:center; }
  .compare .cbar .back { margin-left:-6px; }
  .compare .cbar .spread { flex:1; }
  .compare .crotate { color:var(--ink-soft); }

  /* ---- The scroll -------------------------------------------------------
     touch-action:pan-y, not none: reading down the pair IS the gesture now, so
     the finger's vertical axis belongs to the browser. The horizontal one is
     still ours — a sideways drag walks that bird along the shelf. */
  .compare .cscroll { flex:1; min-height:0; overflow-y:auto;
    overscroll-behavior:contain; touch-action:pan-y; padding-bottom:18px; }

  /* ---- The two names ----------------------------------------------------
     Sticky, so the answer to "which is which" never scrolls away from the
     thing it names. Two columns in BOTH layouts: side by side they sit over
     the cells they label, and stacked they are a control strip, with each cell
     naming its own bird underneath instead (see .cwho). */
  .compare .cnames { position:sticky; top:0; z-index:3;
    display:grid; grid-template-columns:1fr 1fr; gap:10px;
    padding:2px 0 9px; margin-bottom:2px; background:var(--paper);
    box-shadow:0 1px 0 var(--hairline); }
  .cnamecell { min-width:0; display:flex; align-items:center; gap:1px; }
  .cnamecell .cwalk { flex:none; padding:4px; color:var(--ink-soft); }
  .cnamecell .cwalk:disabled { opacity:.28; }
  .cnamecell .cname { flex:1; min-width:0; display:block; text-align:left;
    padding:5px 7px; background:var(--card); border:1.5px solid var(--hairline-strong);
    border-radius:var(--radius); }
  /* The name wraps and the glyph trails it INLINE rather than being ranged
     right: a reserved icon column costs "Black-capped Chickadee" a third line
     in a half-width cell. */
  .cnamecell .cname .common { display:block;
    font-family:var(--font-display); font-variation-settings:var(--fvs-display);
    font-size:13.5px; line-height:1.2; }
  .cnamecell .cname .common .icon { display:inline-flex; vertical-align:-2px;
    margin-left:5px; color:var(--ink-soft); opacity:.7; }
  .cnamecell .cname .sci { display:block; font-size:10.5px; color:var(--ink-soft);
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

  /* ---- The sections ------------------------------------------------------ */
  .compare .cflow { display:flex; flex-direction:column; gap:15px; }
  .csec { display:flex; flex-direction:column; gap:6px; }
  .csec .clabel { display:flex; align-items:center; gap:8px; }
  .csec .clabel .spread { flex:1; }
  /* Play both, per section rather than per screen. With every voice on one
     scroll there is no longer a "current" one for a single button to mean. */
  .csec .cboth { flex:none; gap:5px; padding:4px 9px; font-size:11px;
    border-radius:999px; background:var(--card);
    border:1.5px solid var(--hairline-strong); color:var(--ink-soft); }
  .csec .cboth:hover:not(:disabled), .csec .cboth:focus-visible { color:var(--ink); }
  .csec .cboth:disabled { opacity:.4; }
  /* The pair. THIS is what the rotate control turns, and the only thing it
     turns — same sections, same order, same scroll container. */
  .csec .cpair { display:grid; grid-template-columns:1fr 1fr; gap:10px;
    align-items:start; }
  .compare.rows .csec .cpair { grid-template-columns:1fr; gap:12px; }

  /* ---- One bird's cell in one section ------------------------------------ */
  .ccell { min-width:0; display:flex; flex-direction:column; gap:5px; }
  /* Which bird this cell is. Needed only when the layout has stopped saying so
     by position — stacked, the two cells are above and below rather than left
     and right, and nothing else on the row tells them apart. */
  .ccell .cwho { display:none; }
  /* Smaller and quieter than the section's own label, which is the same small
     caps: the section says WHAT you are looking at and has to win, the cell
     says whose. */
  .compare.rows .ccell .cwho { display:block; font-size:9.4px; opacity:.85; }
  /* The frame is the photograph's own shape (--photo-ratio, published by
     species-photo.ts once the image loads). No height cap: the page scrolls
     now, so a tall portrait costs scroll rather than being letterboxed into a
     box it cannot fill. */
  .ccell .spphoto { width:100%; aspect-ratio:var(--photo-ratio, 4/3);
    border-radius:var(--radius-hand); border:1.5px solid var(--hairline); }
  .csec .cpair > .ccell:nth-child(even) .spphoto { border-radius:var(--radius-hand-alt); }
  /* Stacked, a photograph is led by HEIGHT rather than width, and the width
     follows from its own ratio (the quiz hero's trick, for the same reason).
     Full width, a photo is ~250px tall and a portrait one twice that, so the
     pair no longer fits on a screen together — and a pair you have to scroll
     between is not a comparison, which is the one thing this view cannot give
     up. Sizing by height caps both cells to the same band and costs a
     letterbox nowhere: the frame keeps the picture's shape and simply gets
     narrower. */
  .compare.rows .ccell .spphoto { width:auto; max-width:100%;
    height:min(30vh, 260px); margin:0 auto; }
  .ccell .attrib { text-align:center; }
  /* Nothing to show on this facet. Says which bird and which voice rather than
     "no data", because in a pack the absence is a real fact about the species —
     nobody has ever uploaded a CC-licensed recording of it scolding. */
  .ccell .cgap { display:flex; align-items:center; justify-content:center; gap:7px;
    padding:13px 8px; text-align:center; color:var(--ink-soft); font-style:italic;
    font-size:12px; border:1.5px dashed var(--hairline-strong);
    border-radius:var(--radius-hand); }
  .ccell .cgap .icon { opacity:.5; font-style:normal; flex:none; }

  /* ---- Stepping within a cell -------------------------------------------- */
  .ccell .cstep { display:flex; align-items:center; justify-content:center; gap:2px; }
  .ccell .cstep button { padding:2px 5px; color:var(--ink-soft); }
  .ccell .cstep button:disabled { opacity:.28; }
  .ccell .cstep .n { font-family:var(--font-mono); font-size:10px;
    color:var(--ink-soft); letter-spacing:.05em; min-width:34px; text-align:center; }

  .compare .cmissing { padding:30px 0; display:flex; flex-direction:column;
    align-items:flex-start; gap:10px; }
  .compare [hidden] { display:none; }

  /* ---- The overlay mount ------------------------------------------------- */
  .compare-over { position:fixed; inset:0; z-index:55; background:var(--paper);
    display:flex; flex-direction:column; overflow:hidden; }
  .compare-over > .compare { flex:1; max-width:480px; width:100%; margin:0 auto;
    padding-bottom:env(safe-area-inset-bottom); }

  @media (max-width:359px) {
    .compare { padding-left:12px; padding-right:12px; }
    .cnamecell .cname .common { font-size:13px; }
    /* On the narrowest phone the name already wraps to three lines, and the
       glyph then lands on a fourth of its own — which reads as a stray mark
       rather than as an affordance. The button is still a button. */
    .cnamecell .cname .common .icon { display:none; }
  }
`,
);

/** Controls the browser drives itself; a touch starting on one is not a swipe. */
const FOCUSABLE = 'button, a, input, select, textarea';

/** How far a touch has to travel sideways before it counts as a page turn. */
const SWIPE_PX = 52;

/**
 * How much more horizontal than vertical. Reading down the pair is a vertical
 * drag now, and turning a bird out from under someone who meant to scroll is
 * the most annoying thing a gesture like this can do — so the bar is set high.
 */
const SWIPE_BIAS = 2;

/**
 * A tap within this long of a completed swipe is the swipe's own tail, not a
 * tap. Without it, swiping across a sound strip both turns the page AND plays
 * the recording that just arrived.
 */
const SWIPE_TAIL_MS = 400;

export interface CompareOptions {
  left: string;
  right: string;
  /**
   * The section to open on — SCROLLED to rather than switched to, now that
   * they are all on one page. A Sound ID round hands over the voice its
   * question played, which is the part of the comparison the learner is
   * actually asking about.
   */
  facet?: Facet;
  /**
   * The pair changed. A routed mount rewrites the address bar from this — with
   * ctx.rewrite, which does NOT re-enter the view: the surface has already
   * repainted itself by the time this fires, and walking sideways through a
   * family must not leave twelve entries to press back through. The overlay
   * ignores it.
   */
  onChange?: (left: string, right: string) => void;
  /** What the back arrow does. */
  onBack: () => void;
}

export interface CompareHandle {
  element: HTMLElement;
  /**
   * Bring the section the caller asked for into view.
   *
   * Separate from building, because it needs layout: the surface is built
   * before it is mounted, and an unmounted element has no offsets to scroll
   * to. Both mounts call it once they have appended.
   */
  reveal: () => void;
  destroy: () => void;
}

/**
 * Build the side-by-side surface. The caller owns where it is mounted and what
 * "back" means; everything else — the pickers, the swipe, the playback — is in
 * here.
 */
export function compareSurface(opts: CompareOptions): CompareHandle {
  let left = opts.left;
  let right = opts.right;
  let layout: CompareLayout = getCompareLayout();

  const root = el('div.compare', {});

  // Both codes unknown means both packs are unselected (or the URL was typed).
  // There is no comparison to draw and no picker to offer, because the picker
  // needs a bird to be relevant TO.
  if (!getSpecies(left) && !getSpecies(right)) {
    root.append(
      el('div.cbar', {}, backButton(opts.onBack)),
      el(
        'div.cmissing',
        {},
        el('h1', {}, t('species.compare.missing.title')),
        el('p.meta', {}, t('species.compare.missing.body')),
      ),
    );
    return { element: root, reveal: () => {}, destroy: () => {} };
  }

  const names = el('div.cnames', {});
  const flow = el('div.cflow', {});
  const scroll = el('div.cscroll', {}, names, flow);
  const rotate = rotateButton();

  root.append(
    el('div.cbar', {}, backButton(opts.onBack), el('span.spread', {}), rotate),
    scroll,
  );

  /** Every live section, so playback can be stopped and torn down as a set. */
  let sections: SectionHandle[] = [];

  /**
   * The sequence token for Play both.
   *
   * A strip reports "I stopped" whether it ran out or was cut off by another
   * sound (see WaveformOptions.onEnd), so a chain cannot tell the two apart
   * from the callback alone. This can: every new sequence, and every tap
   * anywhere in the scroll, bumps it, and a callback holding a stale token
   * does nothing. That makes "tap a strip while a pair is playing" mean what
   * it looks like — listen to that one — instead of silently queueing another
   * behind it. ONE token for the whole surface, because one sound at a time is
   * a property of the app rather than of a section: starting the Call pair has
   * to cancel a Song pair still running.
   */
  let sequence = 0;

  function stopSound(): void {
    sequence++;
    for (const s of sections) s.stop();
    stopAll();
  }

  function sideOf(which: 'left' | 'right'): string {
    return which === 'left' ? left : right;
  }

  function setSide(which: 'left' | 'right', code: string): void {
    if (code === sideOf(which)) return;
    stopSound();
    if (which === 'left') left = code;
    else right = code;
    renderAll();
    opts.onChange?.(left, right);
  }

  function setLayout(next: CompareLayout): void {
    layout = next;
    setCompareLayout(next);
    // A class, not a rebuild — see the note at the top of the file.
    paintLayout();
  }

  function paintLayout(): void {
    root.classList.toggle('rows', layout === 'rows');
    // The icon shows the layout it will switch TO, which is what makes a
    // wordless toggle readable: you are looking at the thing you will get.
    const toRows = layout === 'columns';
    rotate.replaceChildren(icon(toRows ? 'compare-rows' : 'compare', { size: 20 }));
    const label = toRows
      ? t('species.compare.rotate.toRows')
      : t('species.compare.rotate.toColumns');
    rotate.title = label;
    rotate.setAttribute('aria-label', label);
  }

  function rotateButton(): HTMLButtonElement {
    const btn = el('button.crotate.btn.btn-plain', { type: 'button' }) as HTMLButtonElement;
    btn.addEventListener('click', () => setLayout(layout === 'columns' ? 'rows' : 'columns'));
    return btn;
  }

  // ---- Painting ------------------------------------------------------------

  function renderNames(): void {
    names.replaceChildren(nameCell('left'), nameCell('right'));
  }

  function renderSections(): void {
    for (const s of sections) s.destroy();
    const offered = compareFacets(getSpecies(left), getSpecies(right));
    sections = offered.map((f) => section(f));
    flow.replaceChildren(...sections.map((s) => s.element));
  }

  function renderAll(): void {
    paintLayout();
    renderNames();
    renderSections();
  }

  // ---- One section ---------------------------------------------------------

  interface SectionHandle {
    element: HTMLElement;
    facet: Facet;
    stop: () => void;
    destroy: () => void;
  }

  function section(facet: Facet): SectionHandle {
    const a = cell('left', facet);
    const b = cell('right', facet);

    const label = el('div.clabel.label', {}, FACET_LABEL[facet], el('span.spread', {}));
    /** The sequence number of a Both waiting on this section's first clip. */
    let chaining = 0;

    if (facet !== 'photo') {
      const ready =
        facetCount(getSpecies(left), facet) > 0 && facetCount(getSpecies(right), facet) > 0;
      const both = el(
        'button.cboth.btn',
        {
          type: 'button',
          disabled: !ready,
          title: ready
            ? t(`species.compare.both.${facet}`)
            : t('species.compare.both.unready'),
        },
        icon('volume', { size: 14 }),
        t('species.compare.both.label'),
      ) as HTMLButtonElement;
      both.addEventListener('click', () => {
        stopSound();
        chaining = sequence;
        void a.player?.toggle();
      });
      label.append(both);
    }

    // The first cell's clip ending is what pulls the second one in. Guarded by
    // the token: the callback fires just the same when something else cut the
    // clip off, and a tap on the other strip must not then play it twice.
    a.onEnd = () => {
      if (!chaining || chaining !== sequence) return;
      chaining = 0;
      void b.player?.toggle();
    };

    const element = el(
      'section.csec',
      { dataset: { facet } },
      label,
      el('div.cpair', {}, a.element, b.element),
    );

    return {
      element,
      facet,
      stop: () => {
        chaining = 0;
        a.stop();
        b.stop();
      },
      destroy: () => {
        a.destroy();
        b.destroy();
      },
    };
  }

  // ---- One bird's cell in one section --------------------------------------

  interface CellHandle {
    element: HTMLElement;
    /** The strip currently in this cell, if the section is a sound one. */
    readonly player: WaveformHandle | null;
    /** Called when that strip stops sounding — the Both chain reads it. */
    onEnd?: () => void;
    stop: () => void;
    destroy: () => void;
  }

  function cell(which: 'left' | 'right', facet: Facet): CellHandle {
    const code = sideOf(which);
    const sp = getSpecies(code);
    const box = el('div.ccell', { dataset: { side: which } });

    // Which asset of this bird's several is on show. Local to the cell, so it
    // resets when the bird changes — "photo 2 of 3" means nothing once you have
    // walked to a bird with one.
    let at = 0;
    let player: WaveformHandle | null = null;

    const body = el('div.cbody', {});
    const step = el('div.cstep', {});
    const handle: CellHandle = {
      element: box,
      get player() {
        return player;
      },
      stop: () => player?.stop(),
      destroy: () => {
        player?.stop();
        player = null;
      },
    };

    const assets = (): (PhotoAsset | AudioAsset)[] => {
      if (!sp) return [];
      return facet === 'photo' ? sp.photos : sp.audio.filter((a) => a.type === facet);
    };

    // The stepper is built ONCE and repainted in place, never replaced:
    // replaceChildren removes before it inserts, and removing a focused element
    // blurs it, so rebuilding would take the focus off the arrow being pressed.
    const stepBack = stepArrow('prev');
    const stepOn = stepArrow('next');
    const stepCount = el('span.n', {});

    function stepArrow(dir: 'prev' | 'next'): HTMLButtonElement {
      const btn = el(
        'button.btn.btn-plain',
        { type: 'button' },
        icon(dir === 'prev' ? 'arrow-left' : 'arrow-right', { size: 14 }),
      ) as HTMLButtonElement;
      btn.addEventListener('click', () => {
        const total = assets().length;
        at = Math.min(total - 1, Math.max(0, at + (dir === 'prev' ? -1 : 1)));
        paintBody();
      });
      return btn;
    }

    function paintBody(): void {
      player?.stop();
      player = null;
      const list = assets();
      const asset = list[at];
      if (!sp) {
        body.replaceChildren(gap(t('species.compare.gap.notInPack')));
      } else if (!asset) {
        body.replaceChildren(gap(t(`species.compare.gap.${facet}`, { name: sp.common })));
      } else if (facet === 'photo') {
        const photo = asset as PhotoAsset;
        body.replaceChildren(
          speciesPhoto(photo, { alt: sp.common }).element,
          el('div.attrib.note', {}, photoCredit(photo)),
        );
      } else {
        const clip = asset as AudioAsset;
        player = waveformPlayer(clip, { onEnd: () => handle.onEnd?.() });
        body.replaceChildren(
          player.element,
          el(
            'div.attrib.note',
            {},
            t('species.credit', {
              author: clip.author,
              license: clip.license,
              source: sourceLabel('xeno-canto'),
            }),
          ),
        );
      }
      paintStep(list.length);
    }

    function paintStep(total: number): void {
      if (total < 2) {
        step.replaceChildren();
        return;
      }
      const name = sp?.common ?? '';
      if (step.firstChild !== stepBack) step.replaceChildren(stepBack, stepCount, stepOn);
      stepBack.setAttribute(
        'aria-label',
        facet === 'photo'
          ? t('species.compare.step.prevPhoto', { name })
          : t('species.compare.step.prevRecording', { name }),
      );
      stepOn.setAttribute(
        'aria-label',
        facet === 'photo'
          ? t('species.compare.step.nextPhoto', { name })
          : t('species.compare.step.nextRecording', { name }),
      );
      // Just the position: the section's own label two lines up already says
      // whether these are photographs or recordings, and spelling it out again
      // wrapped the row onto two lines in a half-width cell.
      stepCount.textContent = t('species.compare.step.count', { at: at + 1, total });
      // A pack carries at most two of anything per bird, so a step is nearly
      // always a step onto an END — which disables the button just pressed, and
      // a disabled button drops the focus to the top of the document. Hand it
      // to the arrow facing the other way: the only thing left to press.
      const was = document.activeElement;
      stepBack.disabled = at === 0;
      stepOn.disabled = at === total - 1;
      if (was === stepBack && stepBack.disabled && !stepOn.disabled) stepOn.focus();
      else if (was === stepOn && stepOn.disabled && !stepBack.disabled) stepBack.focus();
    }

    box.append(el('div.cwho.label', {}, sp?.common ?? code), body, step);
    paintBody();
    return handle;
  }

  /**
   * The next bird along on this side, in the Library's taxonomic order,
   * SKIPPING whatever is already on the other side.
   *
   * Without the skip, walking one side one step along a family lands it on the
   * bird facing it, and the screen becomes a comparison of a bird with itself
   * — the one arrangement of two panes that teaches nothing. Stepping over it
   * keeps the gesture meaning "show me the next bird", which is what it looks
   * like it means. The "compare to" list drops it for the same reason.
   *
   * Undefined at the ends of the list rather than wrapping: the list is
   * taxonomic, so its ends are a goose and a finch, and arriving at one from
   * the other reads as a bug — the species page's own rule.
   */
  function step(which: 'left' | 'right', by: 1 | -1): Species | undefined {
    const list = speciesList();
    const other = sideOf(which === 'left' ? 'right' : 'left');
    const at = list.findIndex((s) => s.code === sideOf(which));
    if (at < 0) return undefined;
    for (let i = at + by; i >= 0 && i < list.length; i += by) {
      if (list[i].code !== other) return list[i];
    }
    return undefined;
  }

  /** Walk arrows either side of the name, which opens the "compare to" list. */
  function nameCell(which: 'left' | 'right'): HTMLElement {
    const code = sideOf(which);
    const sp = getSpecies(code);
    const prev = step(which, -1);
    const next = step(which, 1);
    const walk = (to: Species | undefined, dir: 'prev' | 'next') =>
      el(
        'button.cwalk.btn.btn-plain',
        {
          type: 'button',
          disabled: !to,
          title: to?.common ?? '',
          'aria-label': to
            ? t(dir === 'prev' ? 'species.compare.walk.prev' : 'species.compare.walk.next', {
                name: to.common,
              })
            : t(dir === 'prev' ? 'species.detail.step.noPrev' : 'species.detail.step.noNext'),
          onclick: () => to && setSide(which, to.code),
        },
        icon(dir === 'prev' ? 'arrow-left' : 'arrow-right', { size: 16 }),
      );

    const name = el(
      'button.cname',
      {
        type: 'button',
        'aria-label': t(`species.compare.name.${which}`, { name: sp?.common ?? code }),
        title: t('species.compare.name.title'),
      },
      el('span.common', {}, sp?.common ?? code, icon('compare', { size: 13 })),
      el('span.sci', {}, sp?.sci ?? ''),
    ) as HTMLButtonElement;
    name.addEventListener('click', () => {
      // The picker orders itself around the bird you are standing next to, so
      // "compare to" from the left leads with the LEFT bird's confusables.
      // Failing that (a code from an unselected pack), the other side is the
      // only anchor there is, and it is a better one than nothing.
      const anchor = sp ?? getSpecies(sideOf(which === 'left' ? 'right' : 'left'));
      if (!anchor) return;
      openSpeciesPicker(name, {
        anchor,
        exclude: [left, right],
        onPick: (picked) => setSide(which, picked),
      });
    });

    return el('div.cnamecell', {}, walk(prev, 'prev'), name, walk(next, 'next'));
  }

  function gap(message: string): HTMLElement {
    return el('div.cgap', {}, icon('alert', { size: 16 }), message);
  }

  // ---- The sideways swipe --------------------------------------------------
  //
  // The vertical axis is the browser's now — reading down the pair is the
  // gesture the screen is built around — so this is the one gesture left, and
  // it means what it meant before: walk the bird you started the touch on.

  let x0 = 0;
  let y0 = 0;
  let from: 'left' | 'right' | null = null;
  let tracking = false;
  let swipedAt = 0;

  scroll.addEventListener(
    'touchstart',
    (e) => {
      // Two fingers is a pinch, and a touch that starts on a real control
      // belongs to that control — the walk arrows, the stepper, the name. The
      // sound strip is deliberately NOT excluded: stacked, it is most of the
      // width of a cell. The click it would otherwise fire is swallowed below.
      const on = (e.target as HTMLElement | null)?.closest(FOCUSABLE);
      const side = (e.target as HTMLElement | null)?.closest<HTMLElement>('.ccell');
      tracking = e.touches.length === 1 && !on && !!side;
      if (!tracking) return;
      from = (side!.dataset.side as 'left' | 'right') ?? null;
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
    },
    { passive: true },
  );

  scroll.addEventListener(
    'touchend',
    (e) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - x0;
      const dy = t.clientY - y0;
      if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy) * SWIPE_BIAS) return;
      // Swiping left drags this bird off to the left and brings the next one in
      // from the right — the direction a book pages, which is the species page's
      // rule and has to stay the same one here.
      if (from) {
        const to = step(from, dx < 0 ? 1 : -1);
        if (to) setSide(from, to.code);
      }
      swipedAt = Date.now();
    },
    { passive: true },
  );

  // The tail of a swipe, swallowed before it reaches a sound strip. Capture
  // phase, because the strip's own listener is on the strip.
  scroll.addEventListener(
    'click',
    (e) => {
      if (Date.now() - swipedAt < SWIPE_TAIL_MS) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      // Any deliberate tap in the scroll ends a Both chain: tapping one strip
      // means "play this one", not "play this one and then that one".
      sequence++;
    },
    true,
  );

  renderAll();

  const reveal = (): void => {
    // Only for a section the caller actually named AND the pair can show —
    // defaultFacet returning something else means the ask could not be
    // honoured, and the top of the page is the honest answer.
    if (!opts.facet) return;
    if (defaultFacet(getSpecies(left), getSpecies(right), opts.facet) !== opts.facet) return;
    const target = flow.querySelector<HTMLElement>(`.csec[data-facet="${opts.facet}"]`);
    if (!target || !root.isConnected) return;
    // Measured between the two boxes rather than read off offsetTop, which is
    // relative to the nearest POSITIONED ancestor — not this scroller, which
    // is not positioned, so offsetTop counts from somewhere further up and
    // overshoots by however much sits above the scroller.
    //
    // The target is left sitting just under the sticky names bar rather than
    // at the very top, or the section's own label lands underneath the thing
    // that is pinned over it.
    const from = target.getBoundingClientRect().top - scroll.getBoundingClientRect().top;
    scroll.scrollTop = Math.max(0, scroll.scrollTop + from - names.offsetHeight - 6);
  };

  return {
    element: root,
    reveal,
    destroy: () => {
      stopSound();
      for (const s of sections) s.destroy();
    },
  };
}

function backButton(run: () => void): HTMLElement {
  return el(
    'button.back.btn.btn-plain',
    { type: 'button', onclick: run, 'aria-label': t('common.back') },
    icon('arrow-left', { size: 22 }),
  );
}

/**
 * The same surface, thrown over whatever is already on screen.
 *
 * This exists for the quizzes and only for them. A round is state in a view's
 * closure — the questions, the index, the tally, the answer that can still be
 * undone — and the router tears a view down on navigation, so routing to
 * #/compare mid-round would answer "what did I just confuse those two for?" by
 * throwing the round away. An overlay leaves the round mounted underneath,
 * untouched, and closing it puts the learner back exactly where they were.
 *
 * It closes on Escape, on the back arrow, and on ANY navigation: a hashchange
 * means the screen underneath has gone, and an overlay outliving it would be a
 * comparison floating over the wrong page.
 */
export function openCompare(
  left: string,
  right: string,
  opts: { facet?: Facet } = {},
): void {
  const over = el('div.compare-over', {});

  const close = (): void => {
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('hashchange', close);
    surface.destroy();
    over.remove();
    // The round underneath is still mounted and still listening; giving it the
    // focus back is what makes Enter mean "next question" again.
    returnTo?.focus?.();
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    // A "compare to" list open over the surface owns Escape — it registered
    // its own capture handler after this one, so without this check the
    // overlay would close out from under a menu the learner was dismissing.
    if (document.querySelector('.spick')) return;
    e.preventDefault();
    e.stopPropagation();
    close();
  };

  const returnTo = document.activeElement as HTMLElement | null;
  const surface = compareSurface({ left, right, facet: opts.facet, onBack: close });
  over.append(surface.element);
  document.body.append(over);
  window.addEventListener('keydown', onKey, true);
  window.addEventListener('hashchange', close);
  // Focus moves into the overlay, not left on the button behind it — otherwise
  // the quiz's own key handler goes on grading questions the learner cannot
  // see. The container takes it rather than a control, so the first Tab lands
  // on the back arrow.
  surface.element.tabIndex = -1;
  surface.element.focus();
  // Now that it is in the document and has offsets, the voice the round asked
  // about can be scrolled to.
  surface.reveal();
}
